import { skipToken } from '@reduxjs/toolkit/query';
import { NEW_DESIGN_CREATE_CLASS } from 'components/buttons/ListCreateButton';
import PermissionButton from 'components/buttons/PermissionButton';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Skeleton } from 'components/ui/skeleton';
import { Link } from 'react-router-dom';
import { useGetClientIdentificationsQuery } from 'store/api/client-identifications';
import { PartyType } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { StartClientIdentificationDialog } from './StartClientIdentificationDialog';
import { useClientIdentificationPopup } from './clientIdentificationPopup';

const REQUIRED_FEATURE = FeatureFlag.CLIENT_IDENTIFICATION_AND_RISK_ASSESSMENT;

interface Props {
    party: { id: number; name: string; partyType: PartyType | string };
}

/**
 * Starts a client identification for the client, or opens the one in progress. Only with the compliance switch on,
 * for a person or a company, and for a user who may start one: `useHasPermissions` answers any-of, so the three
 * privileges are asked one by one and handed to `PermissionButton` as its authority.
 */
export function StartClientIdentificationButton({ party }: Props) {
    const isNew = useIsNewDesign();
    const isEnabled = useIsFeatureEnabled(REQUIRED_FEATURE);
    const mayUpdateCompliance = useHasPermissions(['UPDATE.COMPLIANCE']);
    const mayCreateContracts = useHasPermissions(['CREATE.CONTRACTS']);
    const mayUpdateContracts = useHasPermissions(['UPDATE.CONTRACTS.PARTY_ACCESS']);
    const isClientParty = party.partyType === PartyType.PERSON || party.partyType === PartyType.COMPANY;
    const mayStart = mayUpdateCompliance && mayCreateContracts && mayUpdateContracts && isClientParty;
    const { data, isLoading } = useGetClientIdentificationsQuery(isEnabled && mayStart ? party.id : skipToken);
    const { openPopup: openModal, closePopup: closeModal } = useClientIdentificationPopup();

    const className = cn(isNew && NEW_DESIGN_CREATE_CLASS);
    if (isLoading) {
        // Holds the button's place while the list says which of the two it is, so the toolbar does not jump.
        return <Skeleton aria-hidden className={cn('w-56', isNew ? 'h-9' : 'h-8')} />;
    }
    if (!data) {
        return null;
    }
    const inProgress = data.items.find((item) => item.status === 'DRAFT');
    if (inProgress) {
        return (
            <PermissionButton
                hasAuthority={mayStart}
                requiredFeature={REQUIRED_FEATURE}
                variant="outlined"
                size="small"
                className={className}
                nativeButton={false}
                role="link"
                render={<Link to={`/contract/${inProgress.contractId}`} />}
            >
                {t('client-identification.open-in-progress')}
            </PermissionButton>
        );
    }
    return (
        <PermissionButton
            hasAuthority={mayStart && data.canStart}
            requiredFeature={REQUIRED_FEATURE}
            variant="outlined"
            size="small"
            className={className}
            onClick={() => openModal(<StartClientIdentificationDialog party={party} onClose={closeModal} />)}
        >
            {t('client-identification.start-button')}
        </PermissionButton>
    );
}
